import { isEmpty } from 'lodash'
import { getLoggedInUserInfo, getSubscribedFeatureDisplayName } from 'utils'
import { userRoleEnums } from 'enums/userRole'

const hasModuleAccess = (userRoles = {}, moduleKey) => {
  if (!moduleKey) {
    return false
  }

  return (
    Object.prototype.hasOwnProperty.call(userRoles, moduleKey) && !isEmpty(userRoles[moduleKey])
  )
}

const hasSubscribedModuleAccess = (subscribedModules = [], moduleKey) =>
  Array.isArray(subscribedModules) && subscribedModules.includes(moduleKey)

const filterMenuItemsByPermissions = (
  items = [],
  userRoles = {},
  subscribedModules = [],
  options = {},
) => {
  const { allowSubscriptionModules = false, alwaysAllowedModules = [] } = options

  return items.reduce((filteredItems, item) => {
    const filteredChildren = item?.children?.length
      ? filterMenuItemsByPermissions(item.children, userRoles, subscribedModules, options)
      : []

    const isAllowed =
      alwaysAllowedModules.includes(item?.module) ||
      hasModuleAccess(userRoles, item?.module) ||
      (allowSubscriptionModules && hasSubscribedModuleAccess(subscribedModules, item?.module))

    if (filteredChildren.length || (!item?.children?.length && isAllowed)) {
      const nextItem = {
        ...item,
      }

      if (filteredChildren.length) {
        nextItem.children = filteredChildren
      } else {
        delete nextItem.children
      }

      filteredItems.push(nextItem)
    }

    return filteredItems
  }, [])
}

const normalizeModuleKey = (moduleKey) => String(moduleKey || '').trim()

const flattenMenuItems = (items = [], flattened = []) => {
  items.forEach((item) => {
    const { children, ...itemData } = item
    const hasNestedMenu = Array.isArray(children) && children.length > 0
    flattened.push({
      ...itemData,
      children: [],
      hasNestedMenu,
    })

    if (hasNestedMenu) {
      flattenMenuItems(children, flattened)
    }
  })

  return flattened
}

const getPreferredMenuNode = (existingNode, nextNode) => {
  if (!existingNode) {
    return nextNode
  }

  if (nextNode?.hasNestedMenu && !existingNode?.hasNestedMenu) {
    return nextNode
  }

  return existingNode
}

const sortMenuTreeByOriginalOrder = (items = []) => {
  items.sort((leftItem, rightItem) => (leftItem?.menuOrder ?? 0) - (rightItem?.menuOrder ?? 0))

  items.forEach((item) => {
    if (Array.isArray(item?.children) && item.children.length) {
      sortMenuTreeByOriginalOrder(item.children)
    }
  })
}

const applyDynamicMenuHierarchy = (items = [], subscribedFeatures = []) => {
  const flatItems = flattenMenuItems(items)
  const nodesByModule = new Map()

  flatItems.forEach((item, index) => {
    const moduleKey = normalizeModuleKey(item?.module)
    if (!moduleKey) {
      return
    }

    const nextNode = {
      ...item,
      children: [],
      menuOrder: index,
    }

    nodesByModule.set(moduleKey, getPreferredMenuNode(nodesByModule.get(moduleKey), nextNode))
  })

  const topLevel = []
  const topLevelSeen = new Set()
  const featureList = Array.isArray(subscribedFeatures) ? subscribedFeatures : []

  featureList.forEach((feature) => {
    const moduleKey = normalizeModuleKey(feature?.module_key)
    if (!moduleKey) {
      return
    }

    const currentNode = nodesByModule.get(moduleKey)
    if (!currentNode) {
      return
    }

    currentNode.title = getSubscribedFeatureDisplayName(feature?.module_key, currentNode.title)

    const parentModuleKey = normalizeModuleKey(feature?.parent_module_key)
    const parentNode = parentModuleKey ? nodesByModule.get(parentModuleKey) : null

    if (parentNode && parentNode !== currentNode) {
      parentNode.children.push(currentNode)
      return
    }

    if (!topLevelSeen.has(moduleKey)) {
      topLevel.push(currentNode)
      topLevelSeen.add(moduleKey)
    }
  })

  sortMenuTreeByOriginalOrder(topLevel)
  topLevel.forEach((item) => sortMenuTreeByOriginalOrder(item.children))

  return topLevel
}

/**
 * The CRM section.
 *
 * Declared in ONE place and used by every role branch below, because CRM access is
 * not decided by role here. It is decided downstream, twice: the tenant's plan
 * features keep or drop the nodes (applyDynamicMenuHierarchy) and the user's
 * permission map keeps or drops each child (filterMenuItemsByPermissions). A role
 * branch that simply omits these entries hides CRM from users the backend has
 * already granted it to — which is exactly what happened to sales roles.
 *
 * Built fresh on every call so the transforms below can never mutate a shared
 * object across renders.
 */
const buildCrmMenuItem = () => ({
  // Parent gate: a tenant without the `crm` plan feature sees none of this.
  title: 'CRM',
  key: 'crm-root',
  url: '/crm/dashboard',
  module: 'crm',
  icon: 'fe fe-briefcase',
  children: [
    {
      title: 'CRM Dashboard',
      url: '/crm/dashboard',
      module: 'crm_dashboard',
      key: 'crm-dashboard',
    },
    {
      title: 'Leads',
      url: '/crm/leads',
      module: 'crm_leads',
      key: 'crm-leads',
    },
    {
      title: 'Pipeline',
      url: '/crm/opportunities',
      module: 'crm_opportunities',
      key: 'crm-opportunities',
    },
    {
      title: 'Companies',
      url: '/crm/companies',
      module: 'crm_companies',
      key: 'crm-companies',
    },
    {
      title: 'Contacts',
      url: '/crm/contacts',
      module: 'crm_contacts',
      key: 'crm-contacts',
    },
    {
      title: 'Follow-ups',
      url: '/crm/activities',
      module: 'crm_activities',
      key: 'crm-activities',
    },
    {
      title: 'Calendar',
      url: '/crm/calendar',
      module: 'crm_calendar',
      key: 'crm-calendar',
    },
    {
      title: 'Reports',
      url: '/crm/reports',
      module: 'crm_reports',
      key: 'crm-reports',
    },
    {
      title: 'CRM Settings',
      url: '/crm/settings',
      module: 'crm_settings',
      key: 'crm-settings',
    },
  ],
})

export default function getMenuData() {
  let menuData = []
  const userRole = getLoggedInUserInfo()?.userRole
  const hasSubscriptionScope = Boolean(getLoggedInUserInfo()?.hasSubscriptionScope)
  const subscribedModules = getLoggedInUserInfo()?.subscribedModules || []
  const subscribedFeatureList = getLoggedInUserInfo()?.subscribedFeatureList || []

  if (userRole === userRoleEnums?.HR || userRole === userRoleEnums?.ADMIN) {
    menuData = [
      {
        title: 'Dashboard',
        key: '2t2ghd',
        url: '/dashboard/gamma',
        module: 'dashboard',
        icon: 'fe fe-home',
      },
      buildCrmMenuItem(),
      {
        title: 'Configuration',
        key: '6rq4ze6',
        url: '/Configuration',
        module: 'configuration',
        icon: 'fe fe-settings',
        children: [
          {
            title: 'Global Type Category',
            url: '/config/global-type-category',
            module: 'global_type_category',
            key: 'e38wke',
          },
          {
            title: 'Global Type',
            url: '/config/global-type',
            module: 'global_type',
            key: 'e38wkesds',
          },
          {
            title: 'Role and Permissions',
            url: '/config/permissions',
            module: 'role_and_permissions',
            key: 'e38wkew',
          },
          {
            title: 'Module Permission',
            url: '/config/module-permission',
            key: 'e38wkex',
            module: 'module_permission',
          },
          {
            title: 'Company Profile',
            url: '/config/company-profile',
            module: 'company_profile',
            key: '4t2mrb',
          },
          {
            title: 'Terms and condition',
            url: '/config/term-and-condition',
            module: 'term_condition',
            key: 'e38wkea',
          },
          {
            title: 'Leave Policy',
            url: '/config/leave-policy',
            module: 'leave_policy',
            key: 'lvoPrus',
          },
          {
            title: 'Payroll Settings',
            url: '/config/payroll-settings',
            module: 'payroll_settings',
            key: 'payrollConfiguration',
            icon: 'fe fe-sliders',
            children: [
              {
                title: 'Payroll Settings',
                url: '/config/payroll-settings',
                module: 'payroll_settings_master',
                key: 'payrollSettings',
              },
              {
                title: 'Salary Components',
                url: '/config/salary-components',
                module: 'salary_component_master',
                key: 'salaryComponents',
              },
              {
                title: 'Salary Structures',
                url: '/config/salary-structures',
                module: 'salary_structure_master',
                key: 'salaryStructures',
              },
              {
                title: 'Compliance',
                url: '/config/payroll-compliance',
                module: 'payroll_compliance',
                key: 'payrollCompliance',
              },
            ],
          },
          {
            title: "Do And Don't",
            url: '/config/doAndDont',
            module: "do_and_don't",
            key: 'doanddont',
          },
          {
            title: 'Custom Template',
            url: '/config/custom-email-template',
            module: 'custom_template',
            key: 'customhhh',
          },
        ],
      },
      {
        title: 'Location',
        url: '/config/location',
        module: 'location',
        key: 'e8848wkdb',
      },
      {
        title: 'Hierarchy',
        url: '/config/hierarchy',
        module: 'hierarchy',
        key: 'e8848whglb',
      },
      {
        title: 'Company Policy',
        url: '/config/company-policy',
        module: 'company_policy',
        key: 'eyu7wker-top',
      },
      {
        title: 'Holiday ',
        url: '/holiday-management',
        module: 'holiday',
        key: 'e38wkeses-top',
      },
      {
        title: 'HR Management',
        url: '/EmployeeManagement',
        module: 'hr_management',
        key: '6rs4ze6',
        icon: 'fe fe-user',
        children: [
          {
            title: 'Talent Onboarding',
            url: '/Talent Onboarding',
            module: 'talent_onboarding',
            key: 'wf001',
            children: [
              {
                title: 'On Board',
                url: '/on-board',
                module: 'on_board',
                key: 'e38ws3412',
              },
              {
                title: 'On Board ATS',
                url: '/on-board-ats',
                module: 'on_board_ats',
                key: 'e38ws3414',
              },
              {
                title: 'Batch',
                url: '/batch',
                module: 'batch',
                key: '8s554s33',
              },
              {
                title: 'Training & Assessment',
                url: '/training-and-assessment',
                module: 'training_and_assessment',
                key: '8s554s22',
              },
            ],
          },
          {
            title: 'Employees',
            url: '/emp/all-employee',
            module: 'employees',
            key: 'e38ws34ke',
          },
          // {
          //   title: 'Agents',
          //   url: '/emp/all-agents',
          //   module: 'agents',
          //   key: 'e38wk3feds',
          // },
          {
            title: 'Roster Management',
            url: '/roster-management',
            module: 'roster_management',
            key: '6rs4ze5',
            icon: 'fe fe-repeat',
            children: [
              {
                title: 'Roster',
                url: '/roster',
                module: 'roster',
                key: 'e38wk3fo',
              },
              {
                title: 'Shift Management',
                url: '/config/roster-shift-master',
                module: 'roster_shift_master',
                key: 'e38wk3fi',
              },
              {
                title: 'Rotation Rules',
                url: '/roster/rotation-rules',
                module: 'roster_rotation_rules',
                key: 'e38wk3fr',
              },
              {
                title: 'Shift Team Mapping',
                url: '/config/shift-team-mapping',
                module: 'shift_team_mapping',
                key: 'e38wk3fs',
              },

              {
                title: 'Weekly Off Rules',
                url: '/weekly-off-rules',
                module: 'weekly_off_rules',
                key: 'mraanjsi',
              },
            ],
          },
          {
            title: 'Attendance',
            url: '/attendance',
            module: 'attendance',
            key: 'e38wk3fesds',
          },
          {
            title: 'Attendance Regularisation ',
            url: '/attendance-regularisation',
            module: 'attendance_regularisation',
            key: 'e38wk3fes',
          },
          {
            title: 'Down Time',
            url: '/down-time',
            module: 'down_time',
            key: 'e38wk3fdk',
          },
          {
            title: 'Calendar',
            url: '/calendar',
            module: 'calender',
            key: 'e38r4eq',
          },
          {
            title: 'Leave Approval',
            url: '/leave-request',
            module: 'leave_approval',
            key: 'e38kjd7',
          },
          {
            title: 'Leave Balance',
            url: '/leave-balance',
            module: 'leave_balance',
            key: 'e38kjd7lb',
          },
          {
            title: 'Comp Off',
            url: '/comp-off',
            module: 'comp_off',
            key: 'compOffModule',
          },
          {
            title: 'Documents',
            url: '/documents',
            module: 'documents',
            key: '4mn45rdg',
          },
          {
            title: 'HR Settings',
            url: '/config/hr-settings',
            module: 'hr_settings',
            key: 'hr-settings',
          },
          // {
          //   title: 'Happy To Help',
          //   url: '/happy-to-help',
          //   module: 'happy_to_help',
          //   key: 'e38r4eqvd',
          // },
        ],
      },
      {
        title: 'ATS',
        url: '/ats',
        module: 'ats',
        key: 'e38wk3fab',
        icon: 'fe fe-dollar-sign',
        children: [
          {
            title: 'Resume List',
            url: '/ats-management',
            module: 'resume_list',
            key: 'g68dr32es',
          },
          // {
          //   title: 'Resume Upload',
          //   url: '/ats/resumes/upload',
          //   module: 'ats',
          //   key: 'g68dr32eu',
          // },
          {
            title: 'Resume Requirements',
            url: '/ats/resume-requirements',
            module: 'resume_requirements',
            key: 'g68dr32er',
          },
        ],
      },

      {
        title: 'Automation',
        url: '/automation',
        module: 'automation',
        icon: 'fe fe-git-branch',
        key: 'automation',
        children: [
          {
            title: 'Process Automation',
            url: '/process-automation',
            module: 'process_automation',
            key: 'process_automation',
          },
          {
            title: 'Review & Approvals',
            url: '/process-automation/tasks',
            module: 'review_and_approval',
            key: 'process-automation-review-and-approval',
          },
        ],
      },
      {
        title: 'Payroll Management',
        url: '/payroll-dashboard',
        module: 'payroll_management',
        key: 'e38wk3feb',
        icon: 'fe fe-dollar-sign',
        children: [
          {
            title: 'Payroll Dashboard',
            url: '/payroll-dashboard',
            module: 'payroll_dashboard',
            key: 'e38wr34ek',
          },
          {
            title: 'Payroll Processing',
            url: '/payroll-processing',
            module: 'payroll_processing',
            key: 'payrollProcessing',
          },
          {
            title: 'Salary',
            url: '/salary',
            module: 'salary',
            key: '8s544rde4',
          },
          {
            title: 'Bonuses & Incentives',
            url: '/bonuses-incentives',
            module: 'bonuses_and_incentives',
            key: 'bonusesAndIncentives',
          },
          {
            title: 'Reimbursements',
            url: '/reimbursement',
            module: 'reimbursement',
            key: '8s554sde4',
          },
          {
            title: 'Employee Self Service',
            url: '/emp-self-service',
            module: 'employee_self_service',
            key: 'employeeSelfService',
          },
          {
            title: 'Full & Final Settlement',
            url: '/fnf-settlement',
            module: 'fnf_settlement',
            key: 'fullAndFinalSettlement',
          },
        ],
      },
      {
        title: 'Tickets',
        url: '/ticket-management',
        module: 'ticket_management',
        key: '6rds4zf8',
        icon: 'fe fe-lock',
      },
      {
        title: 'Punching Report',
        url: '/punching-report',
        module: 'punching_report',
        key: '8s5547788te43',
        icon: 'fe fe-clock',
      },
      {
        title: 'RAG Status',
        url: '/RAGstatus',
        module: 'ryg_status',
        key: '6rds4ze6',
        icon: 'fe fe-flag',
      },
      {
        title: 'Resignation',
        url: '/resignation',
        module: 'resignation',
        key: '6rds4ze1',
        icon: 'fa fa-sign-out',
      },
      {
        title: 'Reports',
        url: '/report',
        module: 'reports',
        key: '6r767s4ze6',
        icon: 'fe fe-file-text',
        children: [
          {
            title: 'Reports & Insights',
            url: '/reports',
            // Its own module key, not `reports`: applyDynamicMenuHierarchy keys
            // nodes by module and keeps one node per key, so reusing the parent's
            // key would silently drop this row. The feature's parent_module_key
            // is `reports`, which is what nests it here.
            module: 'reports_center',
            key: 'reportsCenter',
          },
          {
            title: 'Attendance Report',
            url: '/attendance-report',
            module: 'attendance_report',
            key: 'e345s34ke',
          },
          {
            title: 'Leave Report',
            url: '/leave-report',
            module: 'leave_report',
            key: 'e38dwk4sds',
          },
          {
            title: 'Activity Logs',
            key: 'auditActivityLogsTop',
            url: '/config/audit-activity-logs',
            module: 'audit_activity_logs',
            icon: 'fe fe-activity',
          },
        ],
      },
      {
        title: 'Teams',
        url: '/teams',
        module: 'teams',
        key: '8rds4zel',
        icon: 'fa fa-group',
      },
      {
        title: 'Acknowledgement',
        url: '/acknowledgement',
        module: 'acknowledgement',
        key: '6rd7854ze1',
        icon: 'fe fe-clipboard',
      },
      {
        title: 'Expenses',
        url: '/expenses',
        module: 'expenses',
        key: '8sd34sde4',
        icon: 'fe fe-dollar-sign',
      },
    ]
  } else {
    menuData = [
      {
        title: 'Dashboard',
        key: '2t2ghd',
        url: '/dashboard/gamma',
        module: 'dashboard',
        icon: 'fe fe-home',
      },
      buildCrmMenuItem(),
      {
        title: 'Employees',
        url: '/emp/all-employee',
        module: 'employees',
        key: 'e38ws34ke',
        icon: 'fe fe-user',
      },
      {
        title: 'Attendance',
        url: '/attendance',
        module: 'attendance',
        key: 'e38wk3fesds',
        icon: 'fa fa-calendar-check-o',
      },
      {
        title: 'On Board',
        url: '/on-board',
        module: 'on_board',
        key: 'e38ws3412',
        icon: 'fa fa-user-plus',
      },
      {
        title: 'On Board ATS',
        url: '/on-board-ats',
        module: 'on_board_ats',
        key: 'e38ws3414',
        icon: 'fa fa-user-plus',
      },
      {
        title: 'Company Policy',
        url: '/config/company-policy',
        module: 'company_policy',
        key: 'eyu7wker',
        icon: 'fe fe-book-open',
      },
      {
        title: 'Location',
        url: '/config/location',
        module: 'location',
        key: 'e8848wkdb',
        icon: 'fe fe-map-pin',
      },
      {
        title: 'Hierarchy',
        url: '/config/hierarchy',
        module: 'hierarchy',
        key: 'e8848whglb',
        icon: 'fe fe-layers',
      },
      {
        title: 'Holiday ',
        url: '/holiday-management',
        module: 'holiday',
        key: 'e38wkeses',
        icon: 'fe fe-gift',
      },
      {
        title: 'Leave Policy',
        url: '/config/leave-policy',
        module: 'leave_policy',
        key: 'lvoPrus',
        icon: 'fe fe-book-open',
      },
      {
        title: "Do And Don't",
        url: '/config/doAndDont',
        module: "do_and_don't",
        key: 'doanddont',
        icon: 'fa fa-check-circle',
      },
      {
        title: 'Staff',
        url: '/staff',
        module: 'staff',
        key: 'e38wk3fe23',
        icon: 'fe fe-users',
      },
      // {
      //   title: 'Agents',
      //   url: '/emp/all-agents',
      //   module: 'agents',
      //   key: 'e38wk3feds',
      //   icon: '  fa fa-user',
      // },
      // {
      //   title: 'Happy To Help',
      //   url: '/happy-to-help',
      //   module: 'happy_to_help',
      //   key: 'e38r4eqvd',
      //   icon: '  fa fa-meh-o',
      // },
      {
        title: 'Down time',
        url: '/down-time',
        module: 'down_time',
        key: 'e38wk3fdk',
        icon: 'fe fe-clock',
        backgroundColor: 'yellow',
      },
      {
        title: 'Roster',
        url: '/roster',
        module: 'roster',
        key: 'e38wk3fo',
        icon: 'fe fe-repeat',
      },
      {
        title: 'Attendance Regularisation ',
        url: '/attendance-regularisation',
        module: 'attendance_regularisation',
        key: 'e38wk3fes',
        icon: 'fa fa-ban',
      },
      {
        title: 'Calendar',
        url: '/calendar',
        module: 'calender',
        key: 'e38r4eq',
        icon: 'fa fa-calendar',
      },
      {
        title: 'Leave Approval',
        url: '/leave-request',
        module: 'leave_approval',
        key: 'e38kjd7',
        icon: 'fe fe-check-square',
        backgroundColor: 'yellow',
      },
      {
        title: 'Leave Balance',
        url: '/leave-balance',
        module: 'leave_balance',
        key: 'e38kjd7lb',
        icon: 'fe fe-calendar',
      },
      {
        title: 'Comp Off',
        url: '/comp-off',
        module: 'comp_off',
        key: 'compOffModuleFlat',
        icon: 'fe fe-clock',
      },
      {
        title: 'Documents',
        url: '/documents',
        module: 'documents',
        key: '4mn45rdg',
      },
      {
        title: 'Tickets',
        url: '/ticket-management',
        module: 'ticket_management',
        key: '6rds4zf8',
        icon: 'fe fe-lock',
      },
      {
        title: 'RAG Status',
        url: '/RYGstatus',
        module: 'ryg_status',
        key: '6rds4ze6',
        icon: 'fe fe-flag',
      },
      {
        title: 'Resignation',
        url: '/resignation',
        module: 'resignation',
        key: '6rds4ze1',
        icon: 'fa fa-sign-out',
      },
      {
        title: 'Acknowledgement',
        url: '/acknowledgement',
        module: 'acknowledgement',
        key: '6rd7854ze1',
        icon: 'fa fa-sign-out',
      },
      {
        title: 'Payroll Management',
        url: '/payroll-dashboard',
        module: 'payroll_management',
        key: '6rds4ze78',
        icon: 'fe fe-flag',
        children: [
          {
            title: 'Payroll Dashboard',
            url: '/payroll-dashboard',
            module: 'payroll_dashboard',
            key: 'payrollDashboardFlat',
          },
          {
            title: 'Payroll Processing',
            url: '/payroll-processing',
            module: 'payroll_processing',
            key: 'payrollProcessingFlat',
          },
          {
            title: 'Salary',
            url: '/salary',
            module: 'salary',
            key: 'payrollSalaryFlat',
          },
          {
            title: 'Bonuses & Incentives',
            url: '/bonuses-incentives',
            module: 'bonuses_and_incentives',
            key: 'bonusesAndIncentivesFlat',
          },
          {
            title: 'Employee Self Service',
            url: '/emp-self-service',
            module: 'employee_self_service',
            key: 'employeeSelfServiceFlat',
          },
          {
            title: 'Reimbursements',
            url: '/reimbursement',
            module: 'reimbursement',
            key: 'payrollReimbursementsFlat',
          },
        ],
      },
      {
        title: 'Automation',
        url: '/automation',
        module: 'automation',
        icon: 'fe fe-git-branch',
        key: 'automation',
        children: [
          {
            title: 'Process Automation',
            url: '/process-automation',
            module: 'process_automation',
            key: 'process_automation',
          },
          {
            title: 'Review & Approvals',
            url: '/process-automation/tasks',
            module: 'review_and_approval',
            key: 'process-automation-review-and-approval',
          },
        ],
      },
      {
        title: 'Reimbursement',
        url: '/reimbursement',
        module: 'reimbursement',
        key: '8s5547788e4',
        icon: 'fe fe-file',
      },
      {
        title: 'Punching Report',
        url: '/punching-report',
        module: 'punching_report',
        key: '8s5547788te6543',
        icon: 'fe fe-clock',
      },
      // Also offered outside the HR/Admin branch: a team lead or manager can run
      // the reports their scope allows, and reportScopeService narrows the rows
      // to the teams they lead.
      {
        title: 'Reports & Insights',
        url: '/reports',
        module: 'reports_center',
        key: 'reportsCenterOps',
        icon: 'fe fe-file-text',
      },
    ]
  }

  menuData = applyDynamicMenuHierarchy(menuData, subscribedFeatureList)

  const userRoles = getLoggedInUserInfo()?.userRoles || {}
  const allowSubscriptionModules = hasSubscriptionScope && userRole === userRoleEnums?.ADMIN
  const alwaysAllowedModules =
    userRole === userRoleEnums?.EMPLOYEE || userRole === userRoleEnums?.USER
      ? ['employee_self_service']
      : []

  if (!hasSubscriptionScope && isEmpty(userRoles) && userRole === userRoleEnums?.ADMIN) {
    return menuData
  }

  return filterMenuItemsByPermissions(menuData, userRoles, subscribedModules, {
    allowSubscriptionModules,
    alwaysAllowedModules,
  })
}
